import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { ExperienceSwitcher } from '../design-v2/hotel/shell/ExperienceSwitcher';
import { stepsLeft } from '../features/onboarding/onboardingChecklist';
import {
  useOnboardingChecklist,
  useOnboardingNavFlags,
} from '../features/onboarding/useOnboardingChecklist';
import {
  Avatar,
  IconBox,
  IconBuilding,
  IconButton,
  IconCheckCircle,
  IconCalendar,
  IconChevronDown,
  IconClose,
  IconDollar,
  IconGrid,
  IconLayers,
  IconMenu,
  IconMessage,
  IconMoon,
  IconPhone,
  IconPlug,
  IconReceipt,
  IconSearch,
  IconSparkle,
  IconStore,
  IconSun,
  IconUsers,
  IconVoicemail,
  Input,
  Menu,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  NavCount,
  PageHeaderNavContext,
} from '../ui/v2';
import { api, apiEndpoints } from '../utils/apiService';
import NewLookWelcome from './NewLookWelcome';
import { useNavCounts } from './useNavCounts';
import { useNavVisibility, type NavVisibility } from './useNavVisibility';
import { isV2Page } from './v2Frame';

/**
 * Design V2 application frame: 240px navy sidebar with the store switcher and the
 * user menu, a `<main>` that owns scrolling, and a drawer below 1024px.
 * Replaces `AppLayout`'s sidebar + header when the V2 frame is on (see
 * `v2Frame.ts`); until the last page is migrated it hosts legacy pages too.
 *
 * This is the restaurant shell (`RestaurantShell` in the hotel IA). Hotel
 * pages have their own, `HotelShell`; the two share no nav. Dual-mode users
 * and platform admins get the `ExperienceSwitcher` above the store switcher.
 *
 * Nav items point at the routes that exist today, so the frame can ship
 * before the pages it frames. Gating is `useNavVisibility`, shared with the
 * legacy sidebar, so the two never disagree about what a store can see.
 *
 * "Events platform" is one item; its pages are tabs inside the events
 * layout (`NavTabs`), not sidebar entries.
 */

type NavItem = {
  name: string;
  path: string;
  icon: React.ReactNode;
  /** Extra path prefixes that keep this item current (nested routes). */
  matchPrefix?: string[];
  show?: (nav: NavVisibility) => boolean;
  /** Shown only until the store is live (the onboarding checklist). */
  onboarding?: boolean;
  /**
   * Also shown before the store is live. Until then the sidebar is just
   * Getting started, Dashboard and Store info (plus staff tools), so setup
   * isn't lost among pages that have nothing to show yet.
   */
  beforeLive?: boolean;
  /** Unread/pending badge, read from `useNavCounts`. */
  count?: (counts: {
    sms: number;
    voicemail: number;
    events: number;
    setup: number;
  }) => number;
  /** Spoken after the number. Defaults to "unread". */
  countWhat?: string;
};

const NAV_GROUPS: Array<{ label?: string; items: NavItem[] }> = [
  {
    items: [
      {
        name: 'Onboarding',
        path: '/onboarding',
        icon: <IconBuilding />,
        matchPrefix: ['/onboarding/'],
        show: (nav) => nav.showGroupOnboarding,
        beforeLive: true,
      },
      {
        name: 'Getting started',
        path: '/getting-started',
        icon: <IconCheckCircle />,
        onboarding: true,
        beforeLive: true,
        count: (c) => c.setup,
        countWhat: 'steps left',
      },
      { name: 'Dashboard', path: '/', icon: <IconGrid />, beforeLive: true },
      {
        name: 'Store info',
        path: '/store-info',
        icon: <IconStore />,
        matchPrefix: ['/store-info/'],
        beforeLive: true,
      },
      { name: 'Calls', path: '/calls', icon: <IconPhone /> },
      {
        name: 'Voicemail',
        path: '/voicemail',
        icon: <IconVoicemail />,
        matchPrefix: ['/voicemail/'],
        count: (c) => c.voicemail,
      },
      {
        name: 'SMS',
        path: '/sms',
        icon: <IconMessage />,
        matchPrefix: ['/sms/'],
        show: (nav) => nav.showSms,
        count: (c) => c.sms,
      },
      { name: 'Orders', path: '/orders', icon: <IconReceipt /> },
      {
        name: 'Menu availability',
        path: '/menu-availability',
        icon: <IconLayers />,
        show: (nav) => nav.showMenuAvailability,
      },
      {
        name: 'Reservations',
        path: '/reservations',
        icon: <IconCalendar />,
        matchPrefix: ['/reservations/'],
      },
      {
        name: 'Events',
        path: '/special-events',
        icon: <IconCalendar />,
        show: (nav) => nav.showEvents,
        count: (c) => c.events,
        countWhat: 'new',
      },
      {
        name: 'Events platform',
        path: '/events',
        icon: <IconSparkle />,
        matchPrefix: ['/events/'],
        show: (nav) => nav.showEventsPlatform,
      },
      {
        name: 'Catering',
        path: '/catering',
        icon: <IconBox />,
        matchPrefix: ['/catering/'],
        show: (nav) => nav.showCatering,
      },
      { name: 'Customers', path: '/customer-tables', icon: <IconUsers /> },
      { name: 'Refunds', path: '/refunds', icon: <IconDollar /> },
      {
        name: 'Integrations',
        path: '/integrations',
        icon: <IconPlug />,
        matchPrefix: ['/integrations/'],
      },
    ],
  },
  {
    label: 'Admin',
    items: [
      {
        name: 'Sales',
        path: '/sales/prospects',
        icon: <IconUsers />,
        matchPrefix: ['/sales/'],
        show: (nav) => nav.isPlatformAdmin,
        beforeLive: true,
      },
      {
        name: 'Platform admin',
        path: '/platform-admin',
        icon: <IconGrid />,
        matchPrefix: ['/platform-admin/'],
        show: (nav) => nav.isPlatformAdmin,
        beforeLive: true,
      },
    ],
  },
];

const matches = (pathname: string, path: string, prefixes: string[] = []) =>
  pathname === path || prefixes.some((prefix) => pathname.startsWith(prefix));

/** Desktop preference for hiding the sidebar behind the menu button. */
export const NAV_COLLAPSED_KEY = 'av2.nav.collapsed';

const readCollapsed = () => {
  try {
    return localStorage.getItem(NAV_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
};

const isDesktop = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(min-width: 1024px)').matches;

type StoreChoices = ReturnType<typeof useStoreChoices>;

function useStoreChoices() {
  const { user } = useAuth();
  const [allStores, setAllStores] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const currentId = Number(
    localStorage.getItem('currentStoreId') || user?.store_id || 0,
  );
  const canSwitch = user?.is_platform_admin || user?.is_store_group_user;

  // Platform admins reach every store; other users only their own list.
  useEffect(() => {
    if (!user?.is_platform_admin) return;
    let alive = true;
    api
      .get(apiEndpoints.getAllStoresUnpaginated())
      .then(async (response) => {
        if (!alive || !response?.ok) return;
        const data = await response.json();
        setAllStores(
          Array.isArray(data)
            ? data
            : Array.isArray(data?.results)
              ? data.results
              : [],
        );
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [user?.is_platform_admin]);

  const choices = useMemo(
    () =>
      user?.is_platform_admin && allStores.length
        ? allStores
        : user?.available_stores || [],
    [allStores, user],
  );
  const current =
    choices.find((store) => store.id === currentId) ||
    (user?.store_name ? { id: currentId, name: user.store_name } : null);

  return { choices, current, currentId, canSwitch };
}

const StoreSwitcher: React.FC<{ store: StoreChoices }> = ({ store }) => {
  const { choices, current, currentId, canSwitch } = store;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const anchor = useRef<HTMLButtonElement>(null);
  const filtered = choices.filter((store) =>
    search.trim()
      ? store.name.toLowerCase().includes(search.toLowerCase()) ||
        String(store.id).includes(search)
      : true,
  );

  const choose = (id: number) => {
    localStorage.setItem('currentStoreId', String(id));
    setOpen(false);
    // Every query is scoped by X-Store-ID; a reload is the honest reset.
    window.location.reload();
  };

  return (
    <div className="relative px-3">
      <button
        ref={anchor}
        type="button"
        disabled={!canSwitch}
        aria-haspopup={canSwitch ? 'listbox' : undefined}
        aria-expanded={canSwitch ? open : undefined}
        onClick={() => setOpen((o) => !o)}
        className={clsx(
          'v2-inverse flex h-12 w-full items-center gap-2.5 rounded-v2 border border-v2-line bg-v2-surface/60 px-2.5 text-left',
          canSwitch && 'hover:bg-v2-sunken',
        )}
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-v2-sm bg-v2-accent/20 text-v2-accent">
          <IconStore size={14} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-v2-13 font-semibold leading-4 text-v2-fg">
            {current?.name || 'Select store'}
          </span>
          {!current && (
            <span className="v2-num block text-v2-11 leading-4 text-v2-fg-3">
              {choices.length} stores
            </span>
          )}
        </span>
        {canSwitch && <IconChevronDown className="text-v2-fg-3" />}
      </button>
      <Menu
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchor}
        width={256}
        className="left-3"
      >
        <div className="p-1 pb-2">
          <Input
            size="sm"
            autoFocus
            aria-label="Find a store"
            placeholder="Find a store…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leading={<IconSearch size={14} />}
          />
        </div>
        <div className="v2-scroll max-h-[280px] overflow-y-auto">
          {filtered.slice(0, 60).map((store) => (
            <MenuItem
              key={store.id}
              selected={store.id === currentId}
              onClick={() => choose(store.id)}
            >
              {store.name}
            </MenuItem>
          ))}
          {filtered.length === 0 && (
            <p className="px-2 py-3 text-v2-12 text-v2-fg-3">
              No stores match.
            </p>
          )}
        </div>
      </Menu>
    </div>
  );
};

const NavLink: React.FC<{
  item: NavItem;
  count: number;
  onNavigate?: () => void;
}> = ({ item, count, onNavigate }) => {
  const { pathname } = useLocation();
  const active = matches(pathname, item.path, item.matchPrefix);
  return (
    <Link
      to={item.path}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={clsx(
        'group relative flex h-9 items-center gap-3 rounded-v2 px-3 text-v2-13 transition-colors',
        active
          ? 'bg-v2-accent-soft/20 font-semibold text-v2-fg before:absolute before:-left-3 before:bottom-2 before:top-2 before:w-[3px] before:rounded-r-full before:bg-v2-accent'
          : 'font-medium text-v2-fg-2 hover:bg-v2-sunken/70 hover:text-v2-fg',
      )}
    >
      <span
        className={clsx(
          'shrink-0',
          active ? 'text-v2-accent' : 'text-v2-fg-3 group-hover:text-v2-fg-2',
        )}
      >
        {item.icon}
      </span>
      <span className="flex-1 truncate">{item.name}</span>
      {/* The badge's own name lands in the link's: "SMS 3 unread". */}
      <NavCount value={count} what={item.countWhat} />
    </Link>
  );
};

const UserMenu: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  const name = `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim();

  const go = (path: string) => {
    setOpen(false);
    onNavigate?.();
    navigate(path);
  };

  return (
    <div className="relative">
      <div className="v2-inverse border-t border-v2-line px-3 py-2.5">
        <button
          ref={anchor}
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={open}
          className="flex h-11 w-full items-center gap-2.5 rounded-v2 px-2 text-left hover:bg-v2-sunken"
        >
          <Avatar name={name} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-v2-13 font-semibold leading-4 text-v2-fg">
              {name || user?.email || '—'}
            </span>
            <span className="block truncate text-v2-11 leading-4 text-v2-fg-3">
              {user?.is_platform_admin ? 'Platform admin' : user?.role || ''}
            </span>
          </span>
          <IconChevronDown className="text-v2-fg-3" />
        </button>
      </div>
      <Menu
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchor}
        className="bottom-14 left-3 mt-0"
        width={208}
      >
        <MenuLabel>{user?.email}</MenuLabel>
        <MenuItem onClick={() => go('/profile')}>Profile</MenuItem>
        <MenuItem onClick={() => go('/billing')}>Usage &amp; billing</MenuItem>
        <MenuItem
          onClick={() => {
            setOpen(false);
            toggleTheme();
          }}
          leading={
            theme === 'dark' ? <IconSun size={14} /> : <IconMoon size={14} />
          }
        >
          {theme === 'dark' ? 'Light appearance' : 'Dark appearance'}
        </MenuItem>
        <MenuSeparator />
        <MenuItem
          tone="danger"
          onClick={() => {
            setOpen(false);
            logout();
            navigate('/signin');
          }}
        >
          Sign out
        </MenuItem>
      </Menu>
    </div>
  );
};

const Sidebar: React.FC<{
  store: StoreChoices;
  onNavigate?: () => void;
  onCollapse?: () => void;
}> = ({ store, onNavigate, onCollapse }) => {
  const nav = useNavVisibility();
  const counts = useNavCounts({
    sms: nav.showSms,
    voicemail: true,
    events: nav.showEvents,
  });
  const checklist = useOnboardingChecklist().data;
  const { showSetup, setupOnly } = useOnboardingNavFlags();
  const allCounts = {
    ...counts,
    setup: checklist && showSetup ? stepsLeft(checklist) : 0,
  };
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();

  const groups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter(
      (item) =>
        (item.show ? item.show(nav) : true) &&
        (!item.onboarding || showSetup) &&
        (!setupOnly || item.beforeLive) &&
        (!needle || item.name.toLowerCase().includes(needle)),
    ),
  })).filter((group) => group.items.length > 0);

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') setQuery('');
    if (e.key !== 'Enter' || !needle) return;
    const first = groups[0]?.items[0];
    if (!first) return;
    setQuery('');
    onNavigate?.();
    navigate(first.path);
  };

  return (
    <aside className="v2-nav flex h-full w-[240px] shrink-0 flex-col">
      <div className="v2-inverse flex h-16 items-center gap-1 px-3">
        {onCollapse && (
          <IconButton label="Hide navigation" onClick={onCollapse}>
            <IconMenu />
          </IconButton>
        )}
        <Link
          to="/"
          className={clsx('inline-flex items-center', !onCollapse && 'px-1')}
          aria-label="AskArthur home"
          onClick={onNavigate}
        >
          {/* The light-on-dark mark: the navigation is navy in both themes. */}
          <img
            src="/images/logo/logo-full-dark.png"
            alt=""
            className="h-8 w-auto"
          />
        </Link>
      </div>
      {/* Restaurant ⇄ Hotel, dual-mode only: renders nothing for anyone else. */}
      <ExperienceSwitcher current="restaurant" onNavigate={onNavigate} />
      <StoreSwitcher store={store} />
      <div className="v2-inverse px-3 pt-3">
        <Input
          size="sm"
          type="search"
          aria-label="Search navigation"
          placeholder="Search navigation"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onSearchKeyDown}
          leading={<IconSearch size={14} />}
        />
      </div>
      <nav
        className="v2-inverse v2-scroll mt-3 flex-1 overflow-y-auto px-3 pb-3"
        aria-label="Primary"
      >
        {groups.length === 0 && (
          <p className="px-3 py-3 text-v2-12 text-v2-fg-3">No pages match.</p>
        )}
        {groups.map(({ items, ...group }, index) => {
          return (
            <div
              key={group.label ?? index}
              className={clsx(index > 0 && 'mt-5')}
            >
              {group.label && (
                <p className="mb-1.5 px-3 text-v2-10 font-semibold uppercase tracking-[0.12em] text-v2-fg-3">
                  {group.label}
                </p>
              )}
              <div className="flex flex-col gap-px">
                {items.map((item) => (
                  <NavLink
                    key={item.path}
                    item={item}
                    count={item.count ? item.count(allCounts) : 0}
                    onNavigate={onNavigate}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </nav>
      <UserMenu onNavigate={onNavigate} />
    </aside>
  );
};

/**
 * Pages that have not filled the frame get their menu button here, next to
 * the store name: always below 1024px, and on desktop while the sidebar is
 * hidden. Their titles do not add a second button.
 */
const LegacyTopBar: React.FC<{
  storeName?: string;
  sidebarHidden: boolean;
  onOpenNav: () => void;
}> = ({ storeName, sidebarHidden, onOpenNav }) => (
  <div
    className={clsx(
      'h-12 shrink-0 items-center gap-2 border-b border-v2-line bg-v2-surface px-4',
      sidebarHidden ? 'flex' : 'flex lg:hidden',
    )}
  >
    <IconButton label="Open navigation" onClick={onOpenNav} className="-ml-2">
      <IconMenu />
    </IconButton>
    {storeName && (
      <span className="truncate text-v2-14 font-semibold">{storeName}</span>
    )}
  </div>
);

const AppFrameV2: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const { pathname } = useLocation();
  const legacyPage = !isV2Page(pathname);
  const store = useStoreChoices();

  useEffect(() => setDrawerOpen(false), [pathname]);

  const setCollapsedPreference = (next: boolean) => {
    setCollapsed(next);
    try {
      localStorage.setItem(NAV_COLLAPSED_KEY, next ? '1' : '0');
    } catch {
      /* preference lasts for this page load */
    }
  };

  // Desktop re-pins the hidden sidebar; smaller screens open the drawer.
  const openNavigation = () => {
    if (collapsed && isDesktop()) setCollapsedPreference(false);
    else setDrawerOpen(true);
  };
  const openNavRef = useRef(openNavigation);
  openNavRef.current = openNavigation;

  const openNav = useMemo(
    () => ({
      open: () => openNavRef.current(),
      sidebarHidden: collapsed,
      frameProvidesNav: legacyPage,
    }),
    [collapsed, legacyPage],
  );

  return (
    <PageHeaderNavContext.Provider value={openNav}>
      <div className="av2 flex h-screen w-full overflow-hidden">
        {!collapsed && (
          <div className="hidden h-full lg:block">
            <Sidebar
              store={store}
              onCollapse={() => setCollapsedPreference(true)}
            />
          </div>
        )}
        {drawerOpen && (
          <div className="fixed inset-0 z-[60] flex lg:hidden">
            <div
              aria-hidden
              className="absolute inset-0 bg-v2-fg/30"
              onClick={() => setDrawerOpen(false)}
            />
            <div className="v2-slide-in-right relative h-full shadow-v2-panel">
              <Sidebar store={store} onNavigate={() => setDrawerOpen(false)} />
              <IconButton
                label="Close navigation"
                onClick={() => setDrawerOpen(false)}
                className="v2-inverse absolute right-3 top-4"
              >
                <IconClose />
              </IconButton>
            </div>
          </div>
        )}
        {/* V2 pages own their scrolling; legacy pages get a scroll container.
            Focusable for the experience switcher's hand-off below 1024px. */}
        <main
          tabIndex={-1}
          className="relative flex min-w-0 flex-1 flex-col overflow-hidden bg-v2-canvas outline-none"
        >
          {legacyPage ? (
            <>
              <LegacyTopBar
                storeName={store.current?.name}
                sidebarHidden={collapsed}
                onOpenNav={openNavigation}
              />
              <div className="v2-scroll flex-1 overflow-y-auto">
                <div className="v2-legacy-page w-full p-4 md:p-6 xl:px-8">
                  <Outlet />
                </div>
              </div>
            </>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
      <NewLookWelcome />
    </PageHeaderNavContext.Provider>
  );
};

export default AppFrameV2;
